import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * A tab whose home is the URL.
 *
 * Same shape as `useState`, so a call site changes by one line — but the value
 * is *derived* from the query string rather than mirrored into state, so the
 * two can never drift apart. That is the bug this avoids: an effect pushing
 * state into the URL while another pulls it back, the two fighting on the way.
 *
 * What it buys: tabs that survive a refresh, links that land on the right one
 * ("open /admin?tab=staff"), Back and Forward following the tabs, and help's
 * "Take me there" being able to name a tab rather than asking the user to find
 * it.
 *
 * @param param    Query key. Nested strips use their own ('tab', then 'pane').
 * @param allowed  Valid values. Anything else in the URL falls back, so a
 *                 mistyped or stale link renders the default rather than an
 *                 empty screen.
 * @param fallback The default, deliberately kept OUT of the URL: /reports, not
 *                 /reports?tab=build.
 */
export function useUrlTab<T extends string>(
  param: string,
  allowed: readonly T[],
  fallback: T,
): [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get(param);
  const value = allowed.includes(raw as T) ? (raw as T) : fallback;

  const setValue = useCallback((next: T) => {
    // Copy rather than mutate: any other parameter on the URL — a nested tab,
    // a filter — has to survive a tab click.
    const params = new URLSearchParams(searchParams);
    if (next === fallback) params.delete(param);
    else params.set(param, next);
    // replace, not push: Back should leave the page, as it does today, rather
    // than walking back through every tab clicked on the way in. Landing on a
    // ?tab= link and pressing Back still returns to it — the value is read from
    // the URL, so browser navigation moves the tabs with it.
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams, param, fallback]);

  return [value, setValue];
}

export default useUrlTab;
